import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Divider,
  MenuItem,
  TextField
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { withStyles } from '@material-ui/core/styles';
import NumberFormat from 'react-number-format';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  createSubscriptionCouponCodeApi,
  updateSubscriptionCouponCodeApi
} from 'api';
import { storeTypeNameUI } from 'helpers/storeTypeNames';


const AutocompleteField = withStyles({
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"]': {
      padding: 0
    }
  }
})(Autocomplete);


const SubscriptionCouponCodeForm = ({
  editableCodeData,
  catalogStoreCodes,
  toggleCouponCodeForm
}: ISubscriptionCouponCodeForm) => {
  const dispatch = useDispatch();
  const { subscriptionCouponCodes = [], stores } = useSelector((state: IReduxState) => state.admin);

  const allCouponCodes = subscriptionCouponCodes.reduce((acc, coupon) => {
    if (editableCodeData && coupon.code === editableCodeData.code) {
      return acc;
    }

    return [...acc, coupon.code.toLowerCase()];
  }, []);

  const storeIdsAddedToCoupones = subscriptionCouponCodes.map((coupon) => coupon.store?._id);
  const notAddedStores = stores.filter((store) => !storeIdsAddedToCoupones.includes(store?._id) && !store.isLive);

  return (
    <Formik
      onSubmit={(values) => {
        editableCodeData
          ? updateSubscriptionCouponCodeApi({dispatch, id: editableCodeData._id, codeData: values})
          : createSubscriptionCouponCodeApi({dispatch, codeData: values});
        toggleCouponCodeForm(false);
      }}
      initialValues={{
        code: editableCodeData?.code || '',
        catalogStoreView: editableCodeData?.catalogStoreView || '',
        storeType: editableCodeData?.storeType || '',
        discountOneTime: editableCodeData?.discountOneTime || 0,
        discountMonthly: editableCodeData?.discountMonthly || 0,
        store: editableCodeData?.store || {}
      }}
      validationSchema={yup.object({
        code: yup
          .string()
          .test('code', 'Code is already exists', (value) => !allCouponCodes.includes(value.toLowerCase()))
          .required('Field is required'),
        catalogStoreView: yup
          .string()
          .required('Field is required'),
        storeType: yup
          .string()
          .required('Field is required'),
        discountOneTime: yup
          .number()
          .required('Field is required'),
        discountMonthly: yup
          .number()
          .required('Field is required')
      })}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        handleSubmit
      }) => (
        <form autoComplete="off" onSubmit={handleSubmit} className="mb-5">
          <TextField
            fullWidth
            required
            variant="outlined"
            label="Code"
            name="code"
            className="mb-5"
            value={values.code}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched?.code && Boolean(errors?.code)}
            helperText={touched?.code && errors?.code}
          />
          <TextField
            fullWidth
            required
            select
            variant="outlined"
            label="Catalog Store View"
            name="catalogStoreView"
            className="mb-5 capitalize"
            value={values.catalogStoreView}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched?.catalogStoreView && Boolean(errors?.catalogStoreView)}
            helperText={touched?.catalogStoreView && errors?.catalogStoreView}
          >
            {catalogStoreCodes.map((storeCode) => (
              <MenuItem
                key={`storeCode-${storeCode}`}
                value={storeCode}
                className="capitalize"
              >
                {storeCode}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            required
            select
            variant="outlined"
            label="Store Type"
            name="storeType"
            className="mb-5 capitalize"
            value={values.storeType}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched?.storeType && Boolean(errors?.storeType)}
            helperText={touched?.storeType && errors?.storeType}
          >
            {Object.keys(storeTypeNameUI).map((type) => (
              <MenuItem
                key={`storeType-${type}`}
                value={type}
              >
                {storeTypeNameUI[type]}
              </MenuItem>
            ))}
          </TextField>
          <NumberFormat
            allowNegative={false}
            thousandSeparator
            className="mb-5"
            prefix="$"
            fullWidth
            required
            variant="outlined"
            name="discountOneTime"
            label="One Time Price Reduction"
            decimalScale={2}
            defaultValue={values.discountOneTime}
            onValueChange={(val) => {
              const price = val.floatValue;
              setFieldValue('discountOneTime', price);
            }}
            onBlur={() => setFieldTouched('discountOneTime', true)}
            customInput={TextField}
            error={touched?.discountOneTime && Boolean(errors?.discountOneTime)}
            helperText={touched?.discountOneTime && errors?.discountOneTime}
          />
          <NumberFormat
            allowNegative={false}
            thousandSeparator
            className="mb-5"
            prefix="$"
            fullWidth
            required
            variant="outlined"
            name="discountMonthly"
            label="Monthly Subscription Price Reduction"
            decimalScale={2}
            defaultValue={values.discountMonthly}
            onValueChange={(val) => {
              const price = val.floatValue;
              setFieldValue('discountMonthly', price);
            }}
            onBlur={() => setFieldTouched('discountMonthly', true)}
            customInput={TextField}
            error={touched?.discountMonthly && Boolean(errors?.discountMonthly)}
            helperText={touched?.discountMonthly && errors?.discountMonthly}
          />
          <AutocompleteField
            disableClearable
            options={notAddedStores}
            getOptionLabel={(option: {apiUrl: string}) => option?.apiUrl || ''}
            getOptionSelected={(option: {_id: string}, value: {_id: string}) => option._id === value._id}
            onChange={(e, newValue) => setFieldValue('store', newValue)}
            value={values.store}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Store"
                name="store"
                required
                variant="outlined"
              />
            )}
          />
          <Divider className="my-5" />
          <div className="flex justify-between pt-5">
            <Button
              variant="outlined"
              onClick={() => toggleCouponCodeForm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
            >
              Submit
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default SubscriptionCouponCodeForm;
